/**
 * Chart Module
 * Handles all chart/graph functionality using Highcharts
 * - Load chart data from API
 * - Render AWLR charts
 * - Render ARR charts (rainfall)
 * - Render AWS charts (weather)
 * - Render Flow Meter charts
 * - Handle chart resize
 *
 * @requires Highcharts
 * @requires DashboardConfig
 * @requires DashboardAPI
 */

const ChartModule = (() => {
  // Private variables
  let _currentChart = null;
  let _currentChartPenampang = null;
  let _currentChartCH = null; // For AWLR+ARR rainfall chart
  let _chartConfig = {};

  /** Tinggi maksimum grafik 12 jam di sidebar dashboard */
  const CHART_12H_HEIGHT = 220;

  // Private methods

  /**
   * Format numeric value for tooltips:
   * - integer => no decimal
   * - decimal => max 3 decimal digits (trim trailing zeros)
   */
  const _formatTooltipNumber = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return value;
    }

    if (
      typeof DashboardHelpers !== "undefined" &&
      typeof DashboardHelpers.formatNumber === "function"
    ) {
      return DashboardHelpers.formatNumber(numericValue);
    }

    return numericValue.toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  };

  /**
   * Batasi tinggi container grafik 12 jam agar sidebar tidak terlalu panjang
   */
  const _setChartContainerHeight = (
    containerId = 'chart-data',
    height = CHART_12H_HEIGHT,
  ) => {
    $(`#${containerId}`).css({
      height: `${height}px`,
      maxHeight: `${height}px`,
    });
  };

  /**
   * Initialize Highcharts global options
   */
  const _initHighchartsOptions = () => {
    Highcharts.setOptions({
      colorScheme: 'light',
      chart: {
        backgroundColor: '#ffffff',
        plotBackgroundColor: '#ffffff',
        height: CHART_12H_HEIGHT,
        style: {
          fontFamily: DashboardConfig.chart.fontFamily,
          fontSize: DashboardConfig.chart.fontSize,
        },
        zooming: {
          type: DashboardConfig.chart.zoomType,
        },
      },
      yAxis: {
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      xAxis: {
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      credits: {
        enabled: false,
      },
      lang: {
        months: [
          'Januari',
          'Februari',
          'Maret',
          'April',
          'Mei',
          'Juni',
          'Juli',
          'Agustus',
          'September',
          'Oktober',
          'November',
          'Desember',
        ],
        shortMonths: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'Mei',
          'Jun',
          'Jul',
          'Agu',
          'Sep',
          'Okt',
          'Nov',
          'Des',
        ],
        weekdays: [
          'Minggu',
          'Senin',
          'Selasa',
          'Rabu',
          'Kamis',
          'Jumat',
          'Sabtu',
        ],
      },
    });
  };

  /**
   * Destroy existing charts
   */
  const _destroyCharts = () => {
    if (_currentChart) {
      _currentChart.destroy();
      _currentChart = null;
    }

    if (_currentChartCH) {
      _currentChartCH.destroy();
      _currentChartCH = null;
    }

    if (_currentChartPenampang) {
      _currentChartPenampang.destroy();
      _currentChartPenampang = null;
    }
  };

  const _hasValidAwlrArrData = (data) => {
    return (
      _hasValidChartData(data?.awlr) || _hasValidChartData(data?.arr)
    );
  };

  /**
   * Check if chart API response has usable data points
   */
  const _hasValidChartData = (data) => {
    return (
      Array.isArray(data) &&
      data.length > 0 &&
      data.some((entry) => Number.isFinite(Number(entry?.value)))
    );
  };

  const _showChartEmpty = (
    containerId = 'chart-data',
    title = 'Data Grafik Belum Tersedia',
    description = 'Belum ada data pembacaan untuk periode 12 jam terakhir pada pos ini.',
  ) => {
    $(`#${containerId}`).html(
      DashboardHelpers.renderSidebarEmpty({
        icon: 'mdi-chart-line-variant',
        title,
        description,
        compact: true,
      }),
    );
  };

  const _showChartError = (containerId = 'chart-data') => {
    $(`#${containerId}`).html(
      DashboardHelpers.renderSidebarEmpty({
        icon: 'mdi-alert-circle-outline',
        title: 'Gagal Memuat Grafik',
        description: 'Terjadi kesalahan saat memuat data. Silakan coba lagi.',
        variant: 'error',
        compact: true,
      }),
    );
  };

  /**
   * Show loading spinner on chart container
   */
  const _showLoading = (containerId = 'chart-data') => {
    _destroyCharts();

    $(`#${containerId}`).html(
      DashboardHelpers.renderSidebarLoading({
        message: 'Memuat grafik...',
        height: CHART_12H_HEIGHT,
      }),
    );
  };

  const _showLoadingPenampang = (
    containerId = 'chart-data-penampang-sungai',
  ) => {
    _destroyCharts();

    $(`#${containerId}`).html(
      DashboardHelpers.renderSidebarLoading({
        message: 'Memuat penampang...',
        height: CHART_12H_HEIGHT,
      }),
    );
  };

  /**
   * Render AWLR chart (Water Level)
   */
  const _renderAwlrChart = (data, config) => {
    const {
      type,
      siaga1,
      siaga2,
      siaga3,
      wellDepth,
      wellDeptUnit,
      wellElevation,
    } = config;
    // Calculate min/max values
    let maxValue = data.reduce(
      (max, obj) => (obj.value > max ? obj.value : max),
      -Infinity,
    );
    let minValue = data.reduce(
      (min, obj) => (obj.value < min ? obj.value : min),
      Infinity,
    );

    const isDepthChart = type === 'agwlr' || type === 'ow';
    const depthSeriesName =
      type === 'agwlr' ? 'MAT' : type === 'ow' ? 'Muka Air' : 'TMA';
    const depthAxisTitle =
      type === 'agwlr'
        ? 'MAT (m)'
        : type === 'ow'
          ? 'Muka Air thd. Top Pipa (m)'
          : 'TMA (m)';

    if (isDepthChart) {
      minValue = 0;
      const depth = Number(wellDepth);
      const dataMax =
        Number.isFinite(maxValue) && maxValue > 0
          ? maxValue + Math.max(maxValue * 0.1, 0.1)
          : 1;
      if (Number.isFinite(depth) && depth > 0) {
        maxValue = Math.max(depth, dataMax);
      } else if (!Number.isFinite(maxValue) || maxValue <= 0) {
        maxValue = 1;
      } else {
        maxValue = dataMax;
      }
    } else if (siaga1 && maxValue < siaga1) {
      maxValue = siaga1 + 0.15;
    }
    // Prepare series data
    let series = [
      {
        name: depthSeriesName,
        data: data.map((e) => [
          e.millis,
          isDepthChart ? Math.max(0, e.value) : e.value,
        ]),
        fillOpacity: 0.5,
        tooltip: {
          valueSuffix: ' m',
        },
        zIndex: 1,
        type: isDepthChart ? 'line' : 'areaspline',
        color: DashboardConfig.chart.colors.primary,
        marker: {
          radius: DashboardConfig.chart.markerRadius,
        },
        lineWidth: isDepthChart ? 2 : DashboardConfig.chart.lineWidth,
      },
    ];

    // Add siaga lines if available and not depth-style charts (AGWLR/OW)
    if (!isDepthChart && siaga1 && siaga2 && siaga3) {
      const siagaSeries = [
        {
          name: 'AWAS',
          type: 'line',
          data: data.map((el) => [el.millis, siaga1]),
          marker: { enabled: false },
          lineWidth: 1,
          dashStyle: 'Dash',
          color: DashboardConfig.chart.colors.siaga1,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              return this.point.index === 0
                ? `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`
                : null;
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'SIAGA',
          type: 'line',
          data: data.map((el) => [el.millis, siaga2]),
          marker: { enabled: false },
          lineWidth: 1,
          dashStyle: 'Dash',
          color: DashboardConfig.chart.colors.siaga2,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              return this.point.index === 0
                ? `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`
                : null;
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'WASPADA',
          type: 'line',
          data: data.map((el) => [el.millis, siaga3]),
          marker: { enabled: false },
          lineWidth: 1,
          dashStyle: 'Dash',
          color: DashboardConfig.chart.colors.siaga3,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              return this.point.index === 0
                ? `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`
                : null;
            },
          },
          enableMouseTracking: false,
        },
      ];

      series = series.concat(siagaSeries);
    }

    _setChartContainerHeight('chart-data', CHART_12H_HEIGHT);

    // Render chart
    _currentChart = Highcharts.chart('chart-data', {
      chart: {
        type: 'line',
        height: CHART_12H_HEIGHT,
      },
      title: {
        text: 'Data 12 Jam Terakhir',
      },
      xAxis: {
        type: 'datetime',
        useUTC: true,
        gapGridLineWidth: 0,
        gridLineWidth: 0,
        labels: {
          rotation: -45, // atau 45, sesuai arah yang diinginkan
          formatter: function () {
            return Highcharts.dateFormat('%e-%b-%y %H:%M', this.value);
          },
        },
      },

      yAxis: {
        title: {
          text: depthAxisTitle,
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        min: minValue,
        max: maxValue,
        floor: isDepthChart ? 0 : undefined,
        softMin: isDepthChart ? 0 : undefined,
        reversed: isDepthChart ? true : false,
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: true,
            enabled: false,
          },
        },
        line: {
          // Mengubah garis lurus menjadi kurva yang lebih lembut.
          // Properti ini sangat relevan saat data melintasi nilai nol.
          softThreshold: true,
        },
        // Anda juga bisa mengaturnya secara spesifik di series Anda
        series: {
          softThreshold: true,
        },
      },
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: function () {
          // Array nama hari dalam Bahasa Indonesia
          const namaHari = [
            'Minggu',
            'Senin',
            'Selasa',
            'Rabu',
            'Kamis',
            'Jumat',
            'Sabtu',
          ];
          const namaBulan = [
            'Januari',
            'Februari',
            'Maret',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Agustus',
            'September',
            'Oktober',
            'November',
            'Desember',
          ];

          // Ambil tanggal dari point pertama
          const date = new Date(this.x);
          const hari = namaHari[date.getUTCDay()];
          const tanggal = date.getUTCDate();
          const bulan = namaBulan[date.getUTCMonth()];
          const tahun = date.getUTCFullYear();
          const jam = ('0' + date.getUTCHours()).slice(-2);
          const menit = ('0' + date.getUTCMinutes()).slice(-2);

          // Format: Rabu, 12 November 2025 10:55 WIB
          let tooltip = `<b>${hari}, ${tanggal} ${bulan} ${tahun} ${jam}:${menit} ${DashboardServerConfig.timezone}</b>`;

          // Tambahkan data dari setiap series
          this.points.forEach(function (point) {
            if (type == 'agwlr') {
              if (wellDepth > 0 && wellElevation > 0) {
                tooltip += `<br/><span style="color:${point.color}">●</span> ${
                  point.series.name
                }: <b>${_formatTooltipNumber(point.y)} m</b>
                <br/><span style="color:black">●</span> DAT: <b>${
                  _formatTooltipNumber(wellDepth - point.y)
                }  m</b>
                <br/><span style="color:black">●</span> Elv. MAT: <b>${
                  _formatTooltipNumber(wellElevation - point.y)
                }  mdpl</b>`;
              } else if (wellDepth > 0 && wellElevation == 0) {
                tooltip += `<br/><span style="color:${point.color}">●</span> ${
                  point.series.name
                }: <b>${_formatTooltipNumber(point.y)} m</b>
                <br/><span style="color:black">●</span> DAT: <b>${
                  _formatTooltipNumber(wellDepth - point.y)
                }  m</b>
                <br/><span style="color:black">●</span> Elv. MAT: <b> - </b>`;
              } else if (wellDepth == 0 && wellElevation > 0) {
                tooltip += `<br/><span style="color:${point.color}">●</span> ${
                  point.series.name
                }: <b>${_formatTooltipNumber(point.y)} m</b>
                <br/><span style="color:black">●</span> DAT: <b> - </b>
                <br/><span style="color:black">●</span> Elv. MAT: <b>${_formatTooltipNumber(
                  wellElevation - point.y,
                )}  mdpl</b>`;
              } else {
                tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)} m</b>
                <br/><span style="color:black">●</span> DAT: <b> - </b>
                <br/><span style="color:black">●</span> Elv. MAT: <b> - </b>`;
              }
            } else if (type == 'ow') {
              tooltip += `<br/><span style="color:${point.color}">●</span> ${
                point.series.name
              }: <b>${_formatTooltipNumber(point.y)} m</b>`;
              if (wellElevation > 0) {
                tooltip += `<br/><span style="color:black">●</span> Elevasi: <b>${_formatTooltipNumber(
                  wellElevation - point.y,
                )} mdpl</b>`;
              } else {
                tooltip += `<br/><span style="color:black">●</span> Elevasi: <b> - </b>`;
              }
            } else {
              tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)} m</b>`;
            }
          });

          return tooltip;
        },
      },
      series: series.map((s, index) => {
        // Hanya berikan efek area/gradien untuk series utama (index 0)
        if (index === 0) {
          return {
            ...s,
            // TMA: areaspline; AGWLR/OW: line (axis reversed)
            type: isDepthChart ? 'line' : 'areaspline',
            threshold: isDepthChart ? maxValue : null,
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: isDepthChart ? 0 : 1,
                x2: 0,
                y2: isDepthChart ? 1 : 0,
              },
              stops: [
                [0, Highcharts.color(s.color).setOpacity(0.6).get('rgba')],
                [1, Highcharts.color(s.color).setOpacity(0).get('rgba')],
              ],
            },
            lineWidth: 2,
            marker: { enabled: false },
          };
        }

        // Untuk series lainnya (Siaga 1, 2, 3), biarkan tetap tipe 'line' tanpa fill
        return {
          ...s,
          type: 'line',
          fillColor: 'transparent', // Pastikan tidak ada warna area
          lineWidth: 1,
          marker: { enabled: false },
        };
      }),
    });
  };

  /**
   * Render ARR chart (Rainfall)
   */
  const _renderRainfallChart = (data, containerId = 'chart-data') => {
    // Check if data is array of objects
    const isArrayObject =
      Array.isArray(data) &&
      data.every(
        (item) =>
          typeof item === 'object' && item !== null && !Array.isArray(item),
      );

    if (isArrayObject) {
      data = data.map((dt) => [dt.millis, dt.value]);
    }

    // Calculate max value
    let maxValue = data.reduce(
      (max, obj) => (obj[1] > max ? obj[1] : max),
      -Infinity,
    );
    maxValue = maxValue > 20 ? maxValue : 20;

    // Prepare intensity threshold lines
    const dataLength = data.length;
    let hujanRingan = [];
    let hujanSedang = [];
    let hujanLebat = [];
    let hujanSangatLebat = [];

    for (let i = 0; i < dataLength; i++) {
      hujanRingan.push([data[i][0], 0.1]);
      hujanSedang.push([data[i][0], 5]);
      hujanLebat.push([data[i][0], 10]);
      hujanSangatLebat.push([data[i][0], 20]);
    }

    // Set global Highcharts options
    Highcharts.setOptions({
      colorScheme: 'light',
      chart: {
        backgroundColor: '#ffffff',
        plotBackgroundColor: '#ffffff',
        height: CHART_12H_HEIGHT,
        style: {
          fontFamily: 'Lexend',
          fontSize: '0.9em',
        },
        zooming: {
          type: 'xy',
        },
      },
      yAxis: {
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      xAxis: {
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    });

    _setChartContainerHeight(containerId, CHART_12H_HEIGHT);

    // Render chart
    const chartInstance = Highcharts.chart(containerId, {
      chart: {
        type: 'column',
        height: CHART_12H_HEIGHT,
      },
      title: {
        text: 'Grafik Curah Hujan',
      },
      subtitle: {
        text: 'Data 12 Jam Terakhir',
      },
      xAxis: {
        type: 'datetime',
        useUTC: false,
        gapGridLineWidth: 0,
        gridLineWidth: 0,
        labels: {
          formatter: function () {
            return Highcharts.dateFormat('%e-%b-%y %H:%M', this.value);
          },
        },
      },
      yAxis: [
        {
          title: {
            text: 'Curah Hujan (mm)',
          },
          opposite: false,
          gridLineWidth: 0,
          minorGridLineWidth: 0,
        },
      ],
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: function () {
          // Array nama hari dalam Bahasa Indonesia
          const namaHari = [
            'Minggu',
            'Senin',
            'Selasa',
            'Rabu',
            'Kamis',
            'Jumat',
            'Sabtu',
          ];
          const namaBulan = [
            'Januari',
            'Februari',
            'Maret',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Agustus',
            'September',
            'Oktober',
            'November',
            'Desember',
          ];

          // Ambil tanggal dari point pertama
          const date = new Date(this.x);
          const hari = namaHari[date.getUTCDay()];
          const tanggal = date.getUTCDate();
          const bulan = namaBulan[date.getUTCMonth()];
          const tahun = date.getUTCFullYear();
          const jam = ('0' + date.getUTCHours()).slice(-2);
          const menit = ('0' + date.getUTCMinutes()).slice(-2);

          // Format: Rabu, 12 November 2025 10:55 WIB
          let tooltip = `<b>${hari}, ${tanggal} ${bulan} ${tahun} ${jam}:${menit} ${DashboardServerConfig.timezone}</b>`;

          // Tambahkan data dari setiap series
          this.points.forEach(function (point) {
            tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)} mm</b>`;
          });

          return tooltip;
        },
      },
      series: [
        {
          name: 'Curah Hujan',
          data: data,
          fillOpacity: 0.5,
          tooltip: {
            valueSuffix: ' mm',
          },
          zIndex: 1,
          type: 'column',
          color: '#7cb5ec',
          marker: {
            radius: 1.5,
          },
          lineWidth: 1,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return _formatTooltipNumber(this.y);
            },
          },
        },
        {
          name: 'Hujan Ringan',
          data: hujanRingan,
          marker: { enabled: false },
          lineWidth: 1,
          type: 'line',
          dashStyle: 'Dash',
          color: '#7cb5ec',
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              if (this.point.index == 0) {
                return `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`;
              }
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'Hujan Sedang',
          data: hujanSedang,
          marker: { enabled: false },
          lineWidth: 1,
          type: 'line',
          dashStyle: 'Dash',
          color: '#ffda4f',
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              if (this.point.index == 0) {
                return `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`;
              }
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'Hujan Lebat',
          data: hujanLebat,
          marker: { enabled: false },
          lineWidth: 1,
          type: 'line',
          dashStyle: 'Dash',
          color: '#ffa602',
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              if (this.point.index == 0) {
                return `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`;
              }
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'Hujan Sangat Lebat',
          data: hujanSangatLebat,
          marker: { enabled: false },
          type: 'line',
          lineWidth: 1,
          dashStyle: 'Dash',
          color: '#ef5350',
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              if (this.point.index == 0) {
                return `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`;
              }
            },
          },
          enableMouseTracking: false,
        },
      ],
    });

    // Store chart instance
    if (containerId === 'chart-data') {
      _currentChart = chartInstance;
    } else {
      _currentChartCH = chartInstance;
    }
  };

  /**
   * Render AWLR+ARR chart
   */
  const _renderAwlrArrChart = (data, config) => {
    const { siaga1, siaga2, siaga3 } = config;
    const dataAwlr = data.awlr.map((e) => [e.millis, e.value]);
    const dataArr = data.arr.map((e) => [e.millis, e.value]);

    console.log('CHART AWLR');
    console.log(siaga1, siaga2, siaga3);

    // Render rainfall chart first
    _renderRainfallChart(dataArr, 'chart-data-ch');

    // Calculate max value for AWLR
    let maxValue = data.awlr.reduce(
      (max, obj) => (obj.value > max ? obj.value : max),
      -Infinity,
    );
    maxValue = maxValue > siaga1 ? maxValue + 0.5 : siaga1;

    // Prepare siaga lines
    let siagaSeries = [];
    if (siaga1) {
      siagaSeries = [
        {
          name: 'AWAS',
          data: dataAwlr.map((el) => [el[0], siaga1]),
          marker: { enabled: false },
          lineWidth: 1,
          dashStyle: 'Dash',
          color: DashboardConfig.chart.colors.siaga1,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              return this.point.index === 0
                ? `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`
                : null;
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'SIAGA',
          data: dataAwlr.map((el) => [el[0], siaga2]),
          marker: { enabled: false },
          lineWidth: 1,
          dashStyle: 'Dash',
          color: DashboardConfig.chart.colors.siaga2,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              return this.point.index === 0
                ? `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`
                : null;
            },
          },
          enableMouseTracking: false,
        },
        {
          name: 'WASPADA',
          data: dataAwlr.map((el) => [el[0], siaga3]),
          marker: { enabled: false },
          lineWidth: 1,
          dashStyle: 'Dash',
          color: DashboardConfig.chart.colors.siaga3,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter() {
              return this.point.index === 0
                ? `<span style="color:${this.color};font-weight:bold;">${this.series.name}</span>`
                : null;
            },
          },
          enableMouseTracking: false,
        },
      ];
    }

    console.log(siagaSeries);

    // Render AWLR chart
    _setChartContainerHeight('chart-data', CHART_12H_HEIGHT);
    _currentChart = Highcharts.chart('chart-data', {
      chart: {
        height: CHART_12H_HEIGHT,
      },
      title: {
        text: 'Grafik Tinggi Muka Air',
      },
      subtitle: {
        text: 'Data 12 Jam Terakhir',
      },
      xAxis: {
        type: 'datetime',
        useUTC: true,
        gapGridLineWidth: 0,
        gridLineWidth: 0,
        title: {
          text: 'Waktu Pembacaan',
        },
        labels: {
          formatter: function () {
            return Highcharts.dateFormat('%e-%b-%y %H:%M', this.value);
          },
        },
      },
      yAxis: {
        title: {
          text: 'Tinggi Muka Air (m)',
        },
        opposite: false,
        min: null, // ← tambahkan ini
        startOnTick: false,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: function () {
          // Array nama hari dalam Bahasa Indonesia
          const namaHari = [
            'Minggu',
            'Senin',
            'Selasa',
            'Rabu',
            'Kamis',
            'Jumat',
            'Sabtu',
          ];
          const namaBulan = [
            'Januari',
            'Februari',
            'Maret',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Agustus',
            'September',
            'Oktober',
            'November',
            'Desember',
          ];

          // Ambil tanggal dari point pertama
          const date = new Date(this.x);
          const hari = namaHari[date.getUTCDay()];
          const tanggal = date.getUTCDate();
          const bulan = namaBulan[date.getUTCMonth()];
          const tahun = date.getUTCFullYear();
          const jam = ('0' + date.getUTCHours()).slice(-2);
          const menit = ('0' + date.getUTCMinutes()).slice(-2);

          // Format: Rabu, 12 November 2025 10:55 WIB
          let tooltip = `<b>${hari}, ${tanggal} ${bulan} ${tahun} ${jam}:${menit} ${DashboardServerConfig.timezone}</b>`;

          // Tambahkan data dari setiap series
          this.points.forEach(function (point) {
            tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)} m</b>`;
          });

          return tooltip;
        },
      },
      series: [
        {
          name: 'TMA',
          data: dataAwlr,
          fillOpacity: 0.5,
          tooltip: {
            valueSuffix: ' m',
          },
          threshold: null,
          zIndex: 1,
          type: 'areaspline',
          color: DashboardConfig.chart.colors.primary,
          marker: {
            radius: DashboardConfig.chart.markerRadius,
          },
          lineWidth: DashboardConfig.chart.lineWidth,
        },
        ...siagaSeries,
      ],
    });
  };

  /**
   * Render AWS chart (Weather Station)
   */
  const _renderAwsChart = (data, sensor) => {
    // Sensor configuration
    const sensorConfig = {
      humidity: { unit: '%', name: 'Kelembaban' },
      pressure: { unit: 'MB', name: 'Tekanan' },
      solarradiation: { unit: 'W/m2', name: 'Radiasi Matahari' },
      windspeed: { unit: 'km/jam', name: 'Kecepatan Angin' },
      winddirection: { unit: '°', name: 'Arah Angin' },
      temperature: { unit: '°C', name: 'Suhu' },
      evaporation: { unit: 'mm', name: 'Penguapan' },
      rainfall: { unit: 'mm', name: 'Curah Hujan' },
    };

    const config = sensorConfig[sensor] || { unit: '', name: 'Sensor' };

    if (sensor === 'rainfall') {
      _renderRainfallChart(data);
    } else {
      _setChartContainerHeight('chart-data', CHART_12H_HEIGHT);
      _currentChart = Highcharts.chart('chart-data', {
        chart: {
          type: 'line',
          height: CHART_12H_HEIGHT,
        },
        title: {
          text: 'Data 12 Jam Terakhir',
        },
        xAxis: {
          type: 'datetime',
          useUTC: true,
          gapGridLineWidth: 0,
          gridLineWidth: 0,
          labels: {
            formatter: function () {
              return Highcharts.dateFormat('%e-%b-%y %H:%M', this.value);
            },
          },
        },
        yAxis: {
          title: {
            text: `${config.name} (${config.unit})`,
          },
          gridLineWidth: 0,
          minorGridLineWidth: 0,
        },
        tooltip: {
          crosshairs: true,
          shared: true,
          formatter: function () {
            // Array nama hari dalam Bahasa Indonesia
            const namaHari = [
              'Minggu',
              'Senin',
              'Selasa',
              'Rabu',
              'Kamis',
              'Jumat',
              'Sabtu',
            ];
            const namaBulan = [
              'Januari',
              'Februari',
              'Maret',
              'April',
              'Mei',
              'Juni',
              'Juli',
              'Agustus',
              'September',
              'Oktober',
              'November',
              'Desember',
            ];

            // Ambil tanggal dari point pertama
            const date = new Date(this.x);
            const hari = namaHari[date.getUTCDay()];
            const tanggal = date.getUTCDate();
            const bulan = namaBulan[date.getUTCMonth()];
            const tahun = date.getUTCFullYear();
            const jam = ('0' + date.getUTCHours()).slice(-2);
            const menit = ('0' + date.getUTCMinutes()).slice(-2);

            // Format: Rabu, 12 November 2025 10:55 WIB
            let tooltip = `<b>${hari}, ${tanggal} ${bulan} ${tahun} ${jam}:${menit} ${DashboardServerConfig.timezone}</b>`;

            // Tambahkan data dari setiap series
            this.points.forEach(function (point) {
              tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)} ${config.unit}</b>`;
            });

            return tooltip;
          },
        },
        series: [
          {
            name: config.name,
            data: data.map((e) => [e.millis, e.value]),
            fillOpacity: 0.5,
            tooltip: {
              valueSuffix: ` ${config.unit}`,
            },
            zIndex: 1,
            type: 'line',
            color: DashboardConfig.chart.colors.primary,
            marker: {
              radius: DashboardConfig.chart.markerRadius,
            },
            lineWidth: DashboardConfig.chart.lineWidth,
          },
        ],
      });
    }
  };

  const _WQ_SENSOR_META = {
    temperature: { label: 'Suhu', unit: '°C' },
    ph: { label: 'pH', unit: '' },
    orp: { label: 'ORP', unit: 'mV' },
    turbidity: { label: 'Turbidity', unit: 'NTU' },
    cod: { label: 'COD', unit: 'mg/L' },
    salinity: { label: 'Salinity', unit: 'g/L' },
    do: { label: 'DO', unit: 'mg/L' },
    ammonium: { label: 'Ammonium NH₄⁺', unit: 'mg/L' },
    ammonia: { label: 'Ammonia', unit: 'mg/L' },
    waterLevel: { label: 'TMA', unit: 'm' },
    waterLevel1: { label: 'TMA Hulu', unit: 'm' },
    waterLevel2: { label: 'TMA Hilir', unit: 'm' },
  };

  const _WQ_CHART_COLORS = [
    '#7cb5ec',
    '#434348',
    '#90ed7d',
    '#f7a35c',
    '#8085e9',
    '#f15c80',
    '#e4d354',
    '#2b908f',
    '#91e8e1',
  ];

  const _formatWqChartTooltip = function () {
    const namaHari = [
      'Minggu',
      'Senin',
      'Selasa',
      'Rabu',
      'Kamis',
      'Jumat',
      'Sabtu',
    ];
    const namaBulan = [
      'Januari',
      'Februari',
      'Maret',
      'April',
      'Mei',
      'Juni',
      'Juli',
      'Agustus',
      'September',
      'Oktober',
      'November',
      'Desember',
    ];
    const date = new Date(this.x);
    const hari = namaHari[date.getUTCDay()];
    const tanggal = date.getUTCDate();
    const bulan = namaBulan[date.getUTCMonth()];
    const tahun = date.getUTCFullYear();
    const jam = ('0' + date.getUTCHours()).slice(-2);
    const menit = ('0' + date.getUTCMinutes()).slice(-2);
    let tooltip = `<b>${hari}, ${tanggal} ${bulan} ${tahun} ${jam}:${menit} ${DashboardServerConfig.timezone}</b>`;

    if (this.points && this.points.length) {
      this.points.forEach(function (point) {
        const unit = point.series.userOptions.unitSuffix || '';
        const suffix = unit ? ` ${unit}` : '';
        tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)}${suffix}</b>`;
      });
    }

    return tooltip;
  };

  const _mapWqApiPoints = (sensorKey, data) => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .filter((row) => row && Number.isFinite(Number(row.value)))
      .map((row) => [row.millis, Number(row.value)]);
  };

  /** Sumbu Y WQMS: min/max mengikuti nilai data (tanpa padding buatan). */
  const _wqDataAxisRange = (values) => {
    const nums = (values || []).filter((v) => Number.isFinite(v));

    if (nums.length === 0) {
      return { min: 0, max: 1 };
    }

    const min = nums.reduce((a, b) => Math.min(a, b), nums[0]);
    const max = nums.reduce((a, b) => Math.max(a, b), nums[0]);

    if (min === max) {
      const base = Math.abs(min) > 1e-9 ? Math.abs(min) : 1;
      const pad = base * 0.05;
      return { min: min - pad, max: max + pad };
    }

    return { min, max };
  };

  const _wqExtractYFromPoint = (pt) => {
    if (pt == null) {
      return NaN;
    }
    if (Array.isArray(pt)) {
      return Number(pt[1]);
    }
    if (typeof pt === 'object' && 'y' in pt) {
      return Number(pt.y);
    }
    return Number(pt);
  };

  /** Nilai Y dari series yang masih aktif di legenda. */
  const _wqCollectVisibleChartYValues = (chart) => {
    const values = [];
    if (!chart || !chart.series) {
      return values;
    }

    chart.series.forEach((s) => {
      if (!s || s.visible === false || s.options?.isInternal) {
        return;
      }

      const raw = s.options?.wqRawPoints;
      if (Array.isArray(raw) && raw.length) {
        raw.forEach((pt) => {
          const y = _wqExtractYFromPoint(pt);
          if (Number.isFinite(y)) {
            values.push(y);
          }
        });
        return;
      }

      const ys = s.yData || s.processedYData || [];
      ys.forEach((y) => {
        if (Number.isFinite(y)) {
          values.push(y);
        }
      });
    });

    return values;
  };

  const _wqApplyYAxisFromVisibleSeries = (chart) => {
    const yAxis = chart?.yAxis?.[0];
    if (!yAxis) {
      return;
    }

    const values = _wqCollectVisibleChartYValues(chart);
    const hasVisibleSeries = chart.series.some(
      (s) => s && s.visible !== false && !s.options?.isInternal,
    );
    const fallback = chart.wqAxisRangeFull || { min: 0, max: 1 };
    const range = !hasVisibleSeries
      ? { min: 0, max: 1 }
      : values.length > 0
        ? _wqDataAxisRange(values)
        : fallback;

    if (
      !Number.isFinite(range.min) ||
      !Number.isFinite(range.max) ||
      range.min >= range.max
    ) {
      return;
    }

    try {
      yAxis.setExtremes(range.min, range.max, true, false);
    } catch (err) {
      console.warn('WQMS y-axis update skipped:', err);
    }
  };

  const _wqScheduleYAxisFromVisibleSeries = (chart) => {
    if (!chart) {
      return;
    }
    setTimeout(() => {
      if (chart && chart.renderTo) {
        _wqApplyYAxisFromVisibleSeries(chart);
      }
    }, 0);
  };

  const _wqCollectSeriesValues = (seriesPayload) => {
    const values = [];
    (seriesPayload || []).forEach((item) => {
      (item.points || []).forEach((p) => {
        const v = Array.isArray(p) ? p[1] : p.y;
        if (Number.isFinite(v)) {
          values.push(v);
        }
      });
    });
    return values;
  };

  const _renderWaterQualityChart = (seriesPayload) => {
    if (!seriesPayload || seriesPayload.length === 0) {
      _showChartEmpty(
        'chart-data',
        'Sensor WQMS Tidak Tersedia',
        'Perangkat ini belum memiliki sensor kualitas air yang aktif.',
      );
      return;
    }

    const series = [];
    const axisRange = _wqDataAxisRange(_wqCollectSeriesValues(seriesPayload));

    seriesPayload.forEach((item, index) => {
      const color = _WQ_CHART_COLORS[index % _WQ_CHART_COLORS.length];
      const points = item.points?.length ? item.points : [];

      series.push({
        name: item.label,
        type: 'line',
        data: points,
        color: color,
        unitSuffix: item.unit,
        wqRawPoints: points,
        lineWidth: DashboardConfig.chart.lineWidth,
        marker: {
          radius: DashboardConfig.chart.markerRadius,
        },
        showInLegend: true,
        enableMouseTracking: points.length > 0,
        tooltip: {
          valueSuffix: item.unit ? ` ${item.unit}` : '',
        },
      });
    });

    _setChartContainerHeight('chart-data', CHART_12H_HEIGHT);
    _currentChart = Highcharts.chart('chart-data', {
      chart: {
        type: 'line',
        height: CHART_12H_HEIGHT,
      },
      title: {
        text: 'Data 12 Jam Terakhir',
      },
      legend: {
        enabled: seriesPayload.length > 1,
        itemStyle: { fontSize: '10px' },
      },
      plotOptions: {
        series: {
          events: {
            show: function () {
              _wqScheduleYAxisFromVisibleSeries(this.chart);
            },
            hide: function () {
              _wqScheduleYAxisFromVisibleSeries(this.chart);
            },
          },
        },
      },
      xAxis: {
        type: 'datetime',
        useUTC: true,
        gapGridLineWidth: 0,
        gridLineWidth: 0,
        labels: {
          formatter: function () {
            return Highcharts.dateFormat('%H:%M', this.value);
          },
        },
        dateTimeLabelFormats: {
          millisecond: '%H:%M',
          second: '%H:%M',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%d %b %H:%M',
          week: '%d %b',
          month: '%b %Y',
          year: '%Y',
        },
      },
      yAxis: {
        title: { text: null },
        min: axisRange.min,
        max: axisRange.max,
        opposite: false,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: {
          style: { fontSize: '10px' },
        },
      },
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: _formatWqChartTooltip,
      },
      series: series,
    });

    _currentChart.wqAxisRangeFull = axisRange;
  };

  const _loadWaterQualityChart = async (deviceId, sensorKeys) => {
    const keys = Array.isArray(sensorKeys) ? sensorKeys : [];
    if (!deviceId || keys.length === 0) {
      _showChartEmpty(
        'chart-data',
        'Sensor WQMS Tidak Tersedia',
        'Perangkat ini belum memiliki sensor kualitas air yang aktif.',
      );
      return;
    }

    const results = await Promise.all(
      keys.map(async (sensorKey) => {
        const meta = _WQ_SENSOR_META[sensorKey] || {
          label: sensorKey,
          unit: '',
        };
        try {
          const data = await DashboardAPI.getChartData('wq', deviceId, sensorKey);
          return {
            key: sensorKey,
            label: meta.label,
            unit: meta.unit,
            points: _mapWqApiPoints(sensorKey, data),
          };
        } catch (error) {
          console.warn('WQ chart sensor failed:', sensorKey, error);
          return {
            key: sensorKey,
            label: meta.label,
            unit: meta.unit,
            points: [],
          };
        }
      }),
    );

    const seriesWithData = results.filter((item) => item.points?.length > 0);
    _renderWaterQualityChart(seriesWithData);
  };

  const _hasValidPenampangData = (response) => {
    const data = response?.data;
    if (!data) return false;

    const distance = data.cs_distance;
    const elevation = data.cs_elevation;

    return (
      Array.isArray(distance) &&
      distance.length > 0 &&
      Array.isArray(elevation) &&
      elevation.length > 0
    );
  };

  const _hidePenampangSection = () => {
    $('#parentPenampangMelintang').fadeOut(100);
  };

  const _showPenampangEmpty = (
    description = 'Belum ada data penampang melintang sungai untuk pos ini.',
  ) => {
    $('#parentPenampangMelintang').fadeIn(100);
    $('#chart-data-penampang-sungai').html(
      DashboardHelpers.renderSidebarEmpty({
        icon: 'mdi-chart-areaspline',
        title: 'Data Penampang Belum Tersedia',
        description,
        compact: true,
      }),
    );
  };

  /**
   * Render Flow Meter chart
   */
  const _renderPenampangChart = (apiResponse) => {
    // 1. Persiapan Data
    const dataBank = apiResponse.data;
    const csDistance = dataBank.cs_distance;
    const csElevation = dataBank.cs_elevation;

    // 2. Logika Format Tanggal (MENGGUNAKAN UTC)
    //ambil saja apa adanya dari api karena sudah dalam UTC, lalu format sesuai kebutuhan
    //ini data dari api  "reading_at": "2026-04-18T18:52:17.109493+07:00",
    const formatTanggalIndo = (dateString) => {
      // Parse langsung tanpa timezone conversion
      const parts = dateString.split('T');
      const dateParts = parts[0].split('-');
      const timeParts = parts[1].split(':');

      const thn = parseInt(dateParts[0]);
      const bln = parseInt(dateParts[1]) - 1; // Month is 0-indexed
      const tgl = parseInt(dateParts[2]);
      const jam = timeParts[0];
      const menit = timeParts[1];

      // Cek Hari Ini (gunakan local date)
      const now = new Date();
      const isToday =
        tgl === now.getDate() &&
        bln === now.getMonth() &&
        thn === now.getFullYear();

      if (isToday) {
        return `Hari ini pukul ${jam}:${menit} ${DashboardServerConfig.timezone}`;
      } else {
        const namaBulan = [
          'Januari',
          'Februari',
          'Maret',
          'April',
          'Mei',
          'Juni',
          'Juli',
          'Agustus',
          'September',
          'Oktober',
          'November',
          'Desember',
        ];
        return `${tgl} ${namaBulan[bln]} ${thn} ${jam}:${menit} ${DashboardServerConfig.timezone}`;
      }
    };

    const formattedDate = formatTanggalIndo(apiResponse.reading_at);

    // 3. Render Chart
    _currentChart = Highcharts.chart('chart-data-penampang-sungai', {
      chart: {
        style: { fontFamily: 'Lexend, Arial, sans-serif' },
        zooming: { type: 'xy' },
      },
      title: {
        text: formattedDate,
        align: 'center',
        useHTML: false,
        style: {
          color: '#666666',
          fontSize: '11px',
          fontWeight: 'normal',
        },
        margin: 2,
      },
      // ... rest of the config (xAxis, yAxis, series) tetap sama seperti sebelumnya
      xAxis: {
        title: { text: 'Jarak (m)' },
        min: 0,
        max: Math.max(...csDistance),
      },
      yAxis: {
        title: { text: 'Elevasi (mdpl)' },
        min: Math.min(...csElevation) - 1,
        scaleToXAxis: true,
      },
      tooltip: {
        shared: true,
        formatter: function () {
          let s = `<b>Jarak: ${_formatTooltipNumber(this.x)} m</b><br/>`;

          this.points.forEach((point) => {
            if (point.series.name === 'TMA Saat Ini') {
              s += `<span style="color:${point.color}">●</span> TMA: <b>${_formatTooltipNumber(point.y)}</b> mdpl<br/>`;
            } else if (point.series.name === 'Penampang Melintang') {
              s += `<span style="color:${point.color}">●</span> Elevasi Tanah: <b>${_formatTooltipNumber(point.y)}</b> mdpl<br/>`;
            }
          });

          return s;
        },
      },
      series: [
        {
          name: 'TMA Saat Ini',
          type: 'areaspline',
          data: csDistance.map((x) => [x, apiResponse.current_water_level]),
          color: '#0000FF',
          marker: { enabled: false },
          zIndex: 5,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(0,0,255,0.4)'],
              [1, 'rgba(0,0,255,0.0)'],
            ],
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.point.index === 0)
                return `TMA: ${_formatTooltipNumber(this.y)} mdpl`;
            },
          },
        },
        {
          name: 'Penampang Melintang',
          type: 'area',
          data: csDistance.map((x, i) => [x, csElevation[i]]),
          color: '#8b4513',
          zIndex: 1,
          marker: { enabled: false },
        },
        // Tambahkan SIAGA, WASPADA, AWAS di sini jika diperlukan
      ],
    });
  };
  /**
   * Render Flow Meter chart
   */
  const _renderFlowMeterChart = (data) => {
    _setChartContainerHeight('chart-data', CHART_12H_HEIGHT);
    _currentChart = Highcharts.chart('chart-data', {
      chart: {
        type: 'line',
        height: CHART_12H_HEIGHT,
      },
      title: {
        text: 'Data 12 Jam Terakhir',
      },
      xAxis: {
        type: 'datetime',
        useUTC: true,
        gapGridLineWidth: 0,
        gridLineWidth: 0,
        labels: {
          formatter: function () {
            return Highcharts.dateFormat('%e-%b-%y %H:%M', this.value);
          },
        },
      },
      yAxis: {
        title: {
          text: 'Debit',
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: function () {
          // Array nama hari dalam Bahasa Indonesia
          const namaHari = [
            'Minggu',
            'Senin',
            'Selasa',
            'Rabu',
            'Kamis',
            'Jumat',
            'Sabtu',
          ];
          const namaBulan = [
            'Januari',
            'Februari',
            'Maret',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Agustus',
            'September',
            'Oktober',
            'November',
            'Desember',
          ];

          // Ambil tanggal dari point pertama
          const date = new Date(this.x);
          const hari = namaHari[date.getUTCDay()];
          const tanggal = date.getUTCDate();
          const bulan = namaBulan[date.getUTCMonth()];
          const tahun = date.getUTCFullYear();
          const jam = ('0' + date.getUTCHours()).slice(-2);
          const menit = ('0' + date.getUTCMinutes()).slice(-2);

          // Format: Rabu, 12 November 2025 10:55 WIB
          let tooltip = `<b>${hari}, ${tanggal} ${bulan} ${tahun} ${jam}:${menit} ${DashboardServerConfig.timezone}</b>`;

          // Tambahkan data dari setiap series
          this.points.forEach(function (point) {
            tooltip += `<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${_formatTooltipNumber(point.y)}</b>`;
          });

          return tooltip;
        },
      },
      series: [
        {
          name: 'Debit',
          data: data.map((e) => [e.millis, e.value]),
          fillOpacity: 0.5,
          tooltip: {
            valueSuffix: ' ',
          },
          zIndex: 1,
          type: 'line',
          color: DashboardConfig.chart.colors.primary,
          marker: {
            radius: DashboardConfig.chart.markerRadius,
          },
          lineWidth: DashboardConfig.chart.lineWidth,
        },
      ],
    });
  };


  // Public API
  return {
    /**
     * Initialize chart module
     */
    init() {
      _initHighchartsOptions();
      _showChartEmpty(
        'chart-data',
        'Pilih Pos Monitoring',
        'Klik salah satu pos di daftar untuk menampilkan grafik data.',
      );
    },

    /**
     * Load chart data from API
     */
    async loadData(config) {
      try {
        _showLoading();
        _chartConfig = config;

        const {
          type,
          deviceId,
          siaga1,
          siaga2,
          siaga3,
          wellDepth,
          wellDeptUnit,
          wellElevation,
        } = config;

        let sensor = 'no-sensor';

        if (type == 'aws') {
          sensor = $('#aws-sensors').val() || '';
        }

        if (type == 'wq') {
          const wqSensors = Array.isArray(config.wqSensors) ? config.wqSensors : [];
          await _loadWaterQualityChart(deviceId, wqSensors);
          $('#chart-data #loading-chart').remove();
          if ($('#chart-data-ch #loading-chart').length) {
            $('#chart-data-ch #loading-chart').remove();
          }
          return;
        }

        const stationType = type === 'agwlr' ? 'agwlr' : type;
        // Fetch data from API
        const response = await DashboardAPI.getChartData(
          stationType,
          deviceId,
          sensor,
        );

        // Remove loading
        $('#chart-data #loading-chart').remove();
        if ($('#chart-data-ch #loading-chart').length) {
          $('#chart-data-ch #loading-chart').remove();
        }

        if (type === 'awlr_arr') {
          if (!_hasValidAwlrArrData(response)) {
            _destroyCharts();
            _showChartEmpty();
            _showChartEmpty(
              'chart-data-ch',
              'Data Curah Hujan Belum Tersedia',
              'Belum ada data curah hujan untuk periode 12 jam terakhir.',
            );
            return;
          }
        } else if (!_hasValidChartData(response)) {
          _destroyCharts();
          _showChartEmpty();
          return;
        }

        // Render based on type
        switch (type) {
          case 'awlr':
          case 'agwlr':
          case 'ow':
            _renderAwlrChart(
              response,
              {
                type,
                siaga1,
                siaga2,
                siaga3,
                wellDepth,
                wellDeptUnit,
                wellElevation,
              },
            );
            break;

          case 'awlr_arr':
            _renderAwlrArrChart(response, { siaga1, siaga2, siaga3 });
            break;

          case 'aws':
            _renderAwsChart(response, sensor);
            break;

          case 'arr':
            _renderRainfallChart(response);
            break;

          case 'fm':
            _renderFlowMeterChart(response);
            break;

          default:
            console.warn('Unknown chart type:', type);
        }
      } catch (error) {
        console.error('❌ Error loading chart:', error);
        _showChartError();
      }
    },

    async loadDataPenampang(config) {
      try {
        _chartConfig = config;
        const { stationId, hasCrossSection } = config;

        if (!hasCrossSection) {
          _hidePenampangSection();
          return;
        }

        _showLoadingPenampang();
        $('#parentPenampangMelintang').fadeIn(100);

        const response = await DashboardAPI.getChartDataPenampang(stationId);

        if (_hasValidPenampangData(response)) {
          _renderPenampangChart(response);
          $('#chart-data-penampang-sungai #loading-chart').remove();
        } else {
          _showPenampangEmpty();
        }
      } catch (error) {
        console.error('Error loading penampang:', error);
        _showPenampangEmpty(
          'Terjadi kesalahan saat memuat data penampang. Silakan coba lagi.',
        );
      }
    },
    /**
     * Handle chart resize
     */
    handleResize() {
      if (_currentChart) {
        _currentChart.reflow();
      }
      if (_currentChartCH) {
        _currentChartCH.reflow();
      }
    },

    /**
     * Destroy charts
     */
    destroy() {
      _destroyCharts();
    },

    /**
     * Get current chart instance
     */
    getCurrentChart() {
      return _currentChart;
    },
  };
})();

// Expose to global scope
window.ChartModule = ChartModule;
